/**
 * useSessionMode — resolves the user's effective session cart mode for the
 * top-of-shell ModeBanner.
 *
 * Mirror of `CartModeResolver.ResolveAsync` (Function App
 * `apps/PCBJxChangeApi/Services/CartModeResolver.cs`); see access rulebook
 * `project_dev_mode_sandbox_access_model_20260507.md` and behavior rulebook
 * `project_cart_mode_rulebook_20260507.md`.
 *
 * Resolution priority (top wins):
 *   1. `dev.mode.enabled = true` AND user is SuperAdmin AND
 *      session RunLive override active                 → 'Test-Live'
 *   2. `dev.mode.enabled = true`                       → 'Sandbox'
 *   3. user.IsInSandboxMode = true                     → 'Sandbox'
 *   4. user.IsUATEnrolled = true (matching pilot)      → 'UAT-Live'
 *   5. default                                         → 'Production'
 *
 * Per-user flags (`IsInSandboxMode`, `IsUATEnrolled`, `HasTestLiveAccess`,
 * `TestRecipientEmail/Phone`) shipped in Sprint 1 (migration
 * `20260508220000_V3_AddUserModeFlags` applied 2026-05-08). AuthContext
 * surfaces them on the AuthUser; this hook reads them at steps 3 + 4 below.
 *
 * NOTE: This is the *session-level* mode (used by the banner). Cart mode
 * is set ONCE at cart creation from this value and is then immutable per
 * the cart-mode-immutability rule. Adapters always read the cart's stamped
 * mode tag, never this hook.
 */

import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import type { SessionMode } from '@/components/primitives/ModeBanner';

function readBoolSetting(
  systemSettings: Record<string, unknown> | undefined,
  key: string,
): boolean {
  if (!systemSettings) return false;
  const v = systemSettings[key];
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const t = v.trim().toLowerCase();
    return t === 'true' || t === '1' || t === 'yes';
  }
  if (typeof v === 'number') return v !== 0;
  return false;
}

/**
 * Returns the effective session mode for the current user. Always defined —
 * defaults to 'Production' when context is still resolving.
 */
export function useSessionMode(): SessionMode {
  const { user } = useAuth();
  const { config } = useConfig();

  return useMemo<SessionMode>(() => {
    // Auth not yet resolved → safe default. The banner stays hidden until
    // we know who's logged in, avoiding a flash-of-Sandbox on cold load.
    if (!user) return 'Production';

    const devModeEnabled = readBoolSetting(config.systemSettings, 'dev.mode.enabled');
    const isSuperAdmin =
      user.permissionRole === 'SuperAdmin' ||
      (user.roles ?? []).includes('SuperAdmin');

    // Step 1: SuperAdmin RunLive override during Dev Mode → Test-Live.
    // Session-only `SuperAdminRunLiveOverride` flag is NOT YET wired —
    // when it lands (TopBar "Run Live" toggle), this branch will read it
    // off a session context. Until then, Dev-Mode SuperAdmins land in
    // Sandbox alongside everyone else (step 2).
    // const runLive = sessionFlags.superAdminRunLiveOverride === true;
    // if (devModeEnabled && isSuperAdmin && runLive) return 'Test-Live';
    void isSuperAdmin;

    // Step 2: Global Dev Mode forces Sandbox (everyone, incl. SuperAdmin
    // unless they flip Run Live above).
    if (devModeEnabled) return 'Sandbox';

    // Step 3: User-level Sandbox forcing flag (Admin-toggled per officer).
    if (user.isInSandboxMode === true) return 'Sandbox';

    // Step 3b: TEST MODE (Pilot/Test, Bill 2026-10-07) — live Core Director, test
    // customers only. Server-resolved; Training (Sandbox) above wins so the user
    // sees exactly one banner.
    if (user.testMode?.active === true) return 'Test-Only';

    // Step 4: User-level UAT enrollment (Admin-toggled per officer). Until
    // a per-pilot match-rule lands, enrollment alone routes the user to
    // UAT-Live. The pilot-match column will narrow this further when the
    // pilot-cohort schema arrives.
    if (user.isUATEnrolled === true) return 'UAT-Live';

    // Step 5: default.
    return 'Production';
  }, [user, config.systemSettings]);
}
